import createChunkSharedModules from 'BuildArtifacts/chunkSharedModules';

import { logRuntimeError } from 'Diagnostics/diagnostics';
import { jsScriptDomain } from 'Models/serverConfiguration';
import { mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import { getStringProperty } from 'Utils/guards';

type ChunkRegistration = {
  id: string;
  isEnabled: () => boolean;
  load: () => Promise<{ initialize(): void }>;
};

const chunks: ChunkRegistration[] = [
  {
    id: 'partsAdvisor',
    isEnabled: () => !!mainConfig.partsAdvisorEnabled,
    load: () => import(/* webpackMode: "lazy" */ './chunks/partsAdvisor'),
  },
];

const attemptedChunks = new Set<string>();
let chunkRuntimePrepared = false;

function prepareChunkRuntime(): void {
  if (chunkRuntimePrepared) {
    return;
  }
  const scriptUrl = jsScriptDomain && new URL(jsScriptDomain);
  if (scriptUrl && ['http:', 'https:'].includes(scriptUrl.protocol)) {
    // eslint-disable-next-line camelcase
    __webpack_public_path__ = new URL('.', scriptUrl).href;
    // eslint-disable-next-line camelcase
  } else if (!__webpack_public_path__) {
    throw new Error('Optional chunks require an HTTP(S) search script URL or an explicit serve public path.');
  }
  window.Convermax._chunkSharedModules = createChunkSharedModules();
  chunkRuntimePrepared = true;
}

// A chunk the browser refused although the server serves it (scrapers block stylesheets) leaves nothing to fix.
// Webpack's ChunkLoadError names the file in `request`; static files send CORS headers, so the probe can read its status.
async function isServed(error: unknown): Promise<boolean> {
  const url = getStringProperty(error, 'request');
  if (!url) {
    return false;
  }
  try {
    return (await fetch(url, { method: 'HEAD', cache: 'no-store' })).ok;
  } catch {
    return false;
  }
}

async function initializeChunk(chunk: ChunkRegistration): Promise<void> {
  let stage = 'prepare';
  try {
    if (!chunk.isEnabled()) {
      return;
    }
    attemptedChunks.add(chunk.id);
    prepareChunkRuntime();
    stage = 'load';
    const module = await chunk.load();
    stage = 'initialize';
    module.initialize();
  } catch (error: unknown) {
    attemptedChunks.add(chunk.id);
    if (stage !== 'load' || !(await isServed(error))) {
      logRuntimeError({ source: `chunk:${chunk.id}:${stage}`, error });
    }
  }
}

export function initializeChunks(): void {
  for (const chunk of chunks) {
    if (!attemptedChunks.has(chunk.id)) {
      void initializeChunk(chunk);
    }
  }
}
